/**
 * The goal an A/B test's results are read against.
 *
 * `testGoal` is the launch goal. A test can be re-pointed at another goal
 * after launch (`resultsGoal`, saved with `PUT …/results-goal`, history kept);
 * the effective goal is the saved one, else the launch goal mapped to the same
 * types. Clicks are a click set: a preset (sign-up buttons, demo buttons, all
 * clicks) or exactly the elements picked. Numbers come from the opt-in
 * `goalMetrics` block (`?goals=all`, docs/TEMP_EXPERIMENT_GOAL_METRICS_CONTRACT.md);
 * the launch goal reads today's fields, so the page works on today's backend.
 */

import { isCountGoal } from "@/lib/utils/abTestStatistics"
import { classifyCta } from "@/lib/experiments/outcomeLadder"
import { chanceBest, compareProportions, compareRates, formatChance, formatRange } from "@/lib/experiments/bayes"

// ── Contract ────────────────────────────────────────────────────────────

export interface GoalTopRow {
  target: string
  secondary: string | null
  events: number
  visitors: number
}

export interface GoalMetrics {
  version: 1 | 2
  visitors: number
  views: number
  clicks: { events: number; visitors: number; top: GoalTopRow[] }
  forms: { events: number; visitors: number; top: GoalTopRow[] }
  trials: { events: number; visitors: number } | null
  payments: { events: number; visitors: number; revenueCents: number; currency: string | null } | null
  bounces: { bounced: number; visitors: number }
  destination: { path: string; visitors: number } | null
  /** v2: the requested (`clickSet=`) or saved click set, unique visitors deduped across its elements. */
  clickSet: { preset: ClickPreset; events: number; visitors: number } | null
  /** Presets the backend can count (newer backends list "key"); [] when not sent. */
  clickSetPresets: ClickPreset[]
}

export type ResultsGoalType = "clicks" | "forms" | "destination" | "trials" | "payments" | "revenue_per_visitor"
export type ClickPreset = "all" | "signup" | "demo" | "key" | "custom"

export interface ClickSetTarget {
  target: string
  secondary: string | null
}

export interface ClickSet {
  preset: ClickPreset
  /** Custom: exactly these. Presets: the elements the preset matched when saved. */
  targets: ClickSetTarget[]
}

export interface ResultsGoal {
  type: ResultsGoalType
  clicks?: ClickSet
  destination?: string
  changedAt?: string
  changedBy?: string
}

export interface ResultsGoalHistoryEntry {
  resultsGoal: ResultsGoal | null
  changedAt: string
  changedBy?: string
}

const num = (v: unknown) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""))
  return Number.isFinite(n) ? n : 0
}

const parseTop = (rows: unknown): GoalTopRow[] =>
  Array.isArray(rows)
    ? rows
        .filter((r) => r && typeof r === "object")
        .map((r: Record<string, unknown>) => ({
          target: String(r.target ?? ""),
          secondary: r.secondary == null || r.secondary === "" ? null : String(r.secondary),
          events: num(r.events),
          visitors: num(r.visitors),
        }))
    : []

const PRESETS: ClickPreset[] = ["all", "signup", "demo", "key", "custom"]
const GOAL_TYPES: ResultsGoalType[] = ["clicks", "forms", "destination", "trials", "payments", "revenue_per_visitor"]

/**
 * Read a raw `goalMetrics` block (v1 or v2). Null when absent or an unknown
 * version, so the page falls back to today's fields. Counts may arrive as
 * strings (Postgres bigint); a single-element array is unwrapped.
 */
export function parseGoalMetrics(raw: unknown): GoalMetrics | null {
  const g = (Array.isArray(raw) ? raw[0] : raw) as Record<string, any> | null | undefined
  const version = Number(g?.version)
  if (!g || typeof g !== "object" || (version !== 1 && version !== 2)) return null
  const pair = (x: any) => (x && typeof x === "object" ? { events: num(x.events), visitors: num(x.visitors) } : null)
  return {
    version: version as 1 | 2,
    visitors: num(g.visitors),
    views: num(g.views),
    clicks: { ...(pair(g.clicks) ?? { events: 0, visitors: 0 }), top: parseTop(g.clicks?.top) },
    forms: { ...(pair(g.forms) ?? { events: 0, visitors: 0 }), top: parseTop(g.forms?.top) },
    trials: pair(g.trials),
    payments:
      g.payments && typeof g.payments === "object"
        ? {
            events: num(g.payments.events),
            visitors: num(g.payments.visitors),
            revenueCents: num(g.payments.revenueCents),
            currency: g.payments.currency ? String(g.payments.currency) : null,
          }
        : null,
    bounces: { bounced: num(g.bounces?.bounced), visitors: num(g.bounces?.visitors) },
    destination:
      g.destination && typeof g.destination === "object"
        ? { path: String(g.destination.path ?? ""), visitors: num(g.destination.visitors) }
        : null,
    clickSet:
      g.clickSet && typeof g.clickSet === "object" && PRESETS.includes(g.clickSet.preset)
        ? { preset: g.clickSet.preset, events: num(g.clickSet.events), visitors: num(g.clickSet.visitors) }
        : null,
    clickSetPresets: Array.isArray(g.clickSetPresets) ? g.clickSetPresets.filter((x: unknown) => PRESETS.includes(x as ClickPreset)) : [],
  }
}

/** A saved `resultsGoal`, or null when absent or malformed. */
export function parseResultsGoal(raw: unknown): ResultsGoal | null {
  const g = raw as Record<string, any> | null | undefined
  if (!g || typeof g !== "object" || !GOAL_TYPES.includes(g.type)) return null
  const out: ResultsGoal = { type: g.type }
  if (g.type === "clicks") {
    const preset: ClickPreset = PRESETS.includes(g.clicks?.preset) ? g.clicks.preset : "all"
    const targets: ClickSetTarget[] = Array.isArray(g.clicks?.targets)
      ? g.clicks.targets
          .filter((t: any) => t && typeof t === "object")
          .map((t: any) => ({ target: String(t.target ?? ""), secondary: t.secondary == null || t.secondary === "" ? null : String(t.secondary) }))
      : []
    out.clicks = { preset, targets }
  }
  if (g.type === "destination") out.destination = String(g.destination ?? "")
  if (g.changedAt) out.changedAt = String(g.changedAt)
  if (g.changedBy) out.changedBy = String(g.changedBy)
  return out
}

export function parseResultsGoalHistory(raw: unknown): ResultsGoalHistoryEntry[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((e) => e && typeof e === "object" && e.changedAt)
    .map((e: any) => ({
      resultsGoal: parseResultsGoal(e.resultsGoal),
      changedAt: String(e.changedAt),
      changedBy: e.changedBy ? String(e.changedBy) : undefined,
    }))
}

/**
 * The body for `PUT …/results-goal`: only type / clicks / destination (the
 * server sets changedAt / changedBy and rejects anything else). Presets match
 * by rule server-side, so only Custom sends targets.
 */
export function resultsGoalBody(goal: ResultsGoal | null): ResultsGoal | null {
  if (!goal) return null
  if (goal.type === "clicks") {
    const set = goal.clicks ?? { preset: "all", targets: [] }
    return {
      type: "clicks",
      clicks: {
        preset: set.preset,
        targets: set.preset === "custom" ? set.targets.map(({ target, secondary }) => ({ target, secondary })) : [],
      },
    }
  }
  if (goal.type === "destination") return { type: "destination", destination: (goal.destination ?? "").trim() }
  return { type: goal.type }
}

/** Backend limits for a click set and destination (400 beyond them). */
export const CLICK_SET_MAX_TARGETS = 200
export const CLICK_SET_MAX_STRING = 512

// ── Launch goal ─────────────────────────────────────────────────────────

const isClickLaunch = (g: string) => g === "click_through" || g === "click_through_rate" || g === "Click Through Rate"
const isFormLaunch = (g: string) => g === "form_submissions" || g === "Form Submission"
const isDestinationLaunch = (g: string) => g === "page_funnel" || g === "external_page_funnel"

export interface GoalContext {
  launchGoal: string
  finishPageUrl?: string | null
}

/** Goal wording for the launch goal: plural noun and label. */
export function launchGoalCopy(goal: string | null | undefined): { noun: string; label: string } {
  const g = goal ?? ""
  if (isFormLaunch(g)) return { noun: "form submissions", label: "Form submissions" }
  if (isClickLaunch(g)) return { noun: "clicks", label: "Click-through" }
  // "Revenue" at setup counts paid Stripe payments per visitor.
  if (g === "revenue") return { noun: "payments", label: "Payments" }
  if (isDestinationLaunch(g)) return { noun: "funnel completions", label: "Funnel completions" }
  if (g === "session_time") return { noun: "engaged visits", label: "Engaged visits" }
  if (g === "bounce_rate") return { noun: "non-bounce visits", label: "Non-bounce visits" }
  return { noun: "conversions", label: "Conversions" }
}

/**
 * The launch `testGoal` as a results goal. Null for launch goals with no
 * equivalent (session_time, bounce_rate): they stay readable as the launch
 * goal only.
 */
export function launchResultsGoal(ctx: GoalContext): ResultsGoal | null {
  const g = ctx.launchGoal
  if (isClickLaunch(g)) return { type: "clicks", clicks: { preset: "all", targets: [] } }
  if (isFormLaunch(g)) return { type: "forms" }
  if (g === "revenue") return { type: "payments" }
  if (isDestinationLaunch(g)) return { type: "destination", destination: ctx.finishPageUrl ?? "" }
  return null
}

/**
 * Destination key for comparisons. A value starting with "/" is a page on the
 * site; anything else is an external link click (compared without protocol,
 * `www.` and trailing slash): the backend's page_funnel / external_page_funnel.
 */
export function normalizeDestination(value: string | null | undefined): string {
  const raw = (value ?? "").trim()
  if (!raw) return ""
  if (raw.startsWith("/")) return (raw.replace(/\/+$/, "") || "/").toLowerCase()
  return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "").toLowerCase()
}

export const isExternalDestination = (value: string | null | undefined) => {
  const raw = (value ?? "").trim()
  return Boolean(raw) && !raw.startsWith("/")
}

export const clickKey = (target: string, secondary: string | null | undefined) => `${target}#${secondary || ""}`

export function sameGoal(a: ResultsGoal | null, b: ResultsGoal | null): boolean {
  if (!a || !b) return a === b
  if (a.type !== b.type) return false
  if (a.type === "destination") return normalizeDestination(a.destination) === normalizeDestination(b.destination)
  if (a.type === "clicks") {
    const pa = a.clicks?.preset ?? "all"
    const pb = b.clicks?.preset ?? "all"
    if (pa !== pb) return false
    if (pa !== "custom") return true
    const keys = (s?: ClickSet) => new Set((s?.targets ?? []).map((t) => clickKey(t.target, t.secondary)))
    const ka = keys(a.clicks)
    const kb = keys(b.clicks)
    return ka.size === kb.size && [...ka].every((k) => kb.has(k))
  }
  return true
}

/** Saved goal (or null) → the goal the results are read on, and whether it is the launch goal. */
export function effectiveGoal(saved: ResultsGoal | null, ctx: GoalContext): { goal: ResultsGoal | null; isLaunch: boolean } {
  const launch = launchResultsGoal(ctx)
  if (!saved || sameGoal(saved, launch)) return { goal: launch, isLaunch: true }
  return { goal: saved, isLaunch: false }
}

// ── Click presets ───────────────────────────────────────────────────────

export const CLICK_PRESETS: Record<ClickPreset, { label: string; goalName: string; rule: string }> = {
  key: {
    label: "Key clicks",
    goalName: "Key clicks",
    rule: "Sign-up, get started, start trial and book-a-demo buttons: any button whose text or link asks for a sign-up or a sales conversation.",
  },
  signup: {
    label: "Sign-up buttons",
    goalName: "Sign-up button clicks",
    rule: "Buttons whose text or link mentions a free trial, sign up, get started, start for free or create an account.",
  },
  demo: {
    label: "Demo buttons",
    goalName: "Demo button clicks",
    rule: "Buttons whose text or link mentions booking a demo, call or meeting, or talking to sales.",
  },
  all: {
    label: "Every click",
    goalName: "Click-through",
    rule: "Every click on the page, navigation, logo and tabs included, counted per visitor.",
  },
  custom: {
    label: "Custom",
    goalName: "Clicks",
    rule: "Only the elements you tick.",
  },
}

export interface ClickTarget {
  key: string
  target: string
  secondary: string | null
  events: number
  /** Unique visitors who clicked it; null on today's API. */
  visitors: number | null
}

/**
 * True when a preset or custom list includes the element. Presets use the
 * same rule as the backend (`classifyCta` over target + secondary, demo wins
 * over sign-up). Custom: exact (target, secondary); a null secondary means
 * any secondary for that target.
 */
export function clickSetIncludes(set: ClickSet, t: ClickSetTarget): boolean {
  if (set.preset === "all") return true
  if (set.preset === "signup") return classifyCta(t.target, t.secondary) === "trial"
  if (set.preset === "demo") return classifyCta(t.target, t.secondary) === "demo"
  if (set.preset === "key") return classifyCta(t.target, t.secondary) != null
  return set.targets.some((x) => x.target === t.target && (x.secondary == null || x.secondary === (t.secondary ?? null)))
}

/** The Key clicks preset: sign-up or demo buttons. */
export const KEY_CLICKS: ClickSet = { preset: "key", targets: [] }

/**
 * "+ Add goal" input as a destination: a page is a path on the site ("/x",
 * a pasted URL keeps only its path); a link drops protocol, `www.` and the
 * trailing slash (normalizeDestination). Null when empty or too long.
 */
export function destinationInput(raw: string, kind: "page" | "link"): string | null {
  const v = raw.trim()
  if (!v || v.length > 2048) return null
  if (kind === "link") return normalizeDestination(v) || null
  let path = v
  if (/^https?:\/\//i.test(v)) {
    try {
      path = new URL(v).pathname
    } catch {
      return null
    }
  }
  path = path.startsWith("/") ? path : `/${path}`
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path
}

/** A preset's current matches as a custom set, for editing from a preset. */
export function presetToCustom(set: ClickSet, elements: ClickSetTarget[]): ClickSet {
  if (set.preset === "custom") return set
  return {
    preset: "custom",
    targets: elements.filter((e) => clickSetIncludes(set, e)).map(({ target, secondary }) => ({ target, secondary })),
  }
}

/** `?clickSet=` value (URL-encoded by URLSearchParams); same shape the PUT takes. */
export const clickSetParam = (set: ClickSet) => JSON.stringify(resultsGoalBody({ type: "clicks", clicks: set })!.clicks)

// ── Plain names ─────────────────────────────────────────────────────────

export const GOAL_TYPE_LABELS: Record<ResultsGoalType, string> = {
  clicks: "Clicks",
  forms: "Form submissions",
  destination: "Visits a page",
  trials: "Trials",
  payments: "Payments",
  revenue_per_visitor: "Revenue per visitor",
}

/** "Sign-up button clicks", "Visits /thank-you", "Trials started". */
export function goalName(goal: ResultsGoal | null, ctx: GoalContext, nameOf?: (t: ClickSetTarget) => string): string {
  if (!goal) return launchGoalCopy(ctx.launchGoal).label
  switch (goal.type) {
    case "clicks": {
      const set = goal.clicks ?? { preset: "all", targets: [] }
      if (set.preset !== "custom") return CLICK_PRESETS[set.preset].goalName
      if (set.targets.length === 1) return `Clicks on ${nameOf ? nameOf(set.targets[0]) : set.targets[0].target || set.targets[0].secondary || "one element"}`
      return `Clicks on ${set.targets.length} elements`
    }
    case "forms":
      return "Form submissions"
    case "destination": {
      const d = (goal.destination ?? "").trim()
      if (!d) return "Visits a page"
      return isExternalDestination(d) ? `Clicks through to ${d}` : `Visits ${d}`
    }
    case "trials":
      return "Trials started"
    case "payments":
      return "Payments"
    case "revenue_per_visitor":
      return "Revenue per visitor"
  }
}

// ── Per-version sources ─────────────────────────────────────────────────

/** Everything one version can contribute to a goal, from either API shape. */
export interface VariantSource {
  id: string
  label: string
  letter: string
  isControl: boolean
  /** Unique Visitors (the primary request's sessions): the exposure for every goal. */
  visitors: number
  /** The API's launch-goal conversions. */
  launchEvents: number
  goalMetrics: GoalMetrics | null
  /** Today's API: click targets from `topEvents` (top 25 per page). */
  topClicks: ClickTarget[]
  /** Today's API: every click (`totalEvents`), null when the launch goal is a form. */
  totalClicks: number | null
  /** Today's API: Stripe outcomes, present on revenue-goal tests. */
  trials: number | null
  payments: number | null
  /** Dollars. */
  revenue: number | null
}

/** Click targets for one version: `goalMetrics.clicks.top` when present, else `topEvents`. */
export function clickTargets(source: VariantSource): ClickTarget[] {
  if (source.goalMetrics) {
    return source.goalMetrics.clicks.top.map((r) => ({
      key: clickKey(r.target, r.secondary),
      target: r.target,
      secondary: r.secondary,
      events: r.events,
      visitors: r.visitors,
    }))
  }
  return source.topClicks
}

/** Raw `topEvents` rows (today's API) as click targets; form submits are skipped. */
export function topClicksFromEvents(topEvents: unknown): ClickTarget[] {
  if (!Array.isArray(topEvents)) return []
  return topEvents
    .filter((e: any) => e && (!e.action || e.action === "click"))
    .map((e: any) => ({
      key: clickKey(String(e.target ?? ""), e.secondary || null),
      target: String(e.target ?? ""),
      secondary: e.secondary || null,
      events: num(e.event_count ?? e.count),
      visitors: null,
    }))
}

export interface ClickElement extends ClickSetTarget {
  key: string
  /** Per version letter. Unique visitors when the API has them, else click events. */
  byVersion: Record<string, number>
  measure: "visitors" | "events"
  total: number
}

/** Every clicked element across versions, busiest first, for the editor's checklist. */
export function clickElements(sources: VariantSource[]): ClickElement[] {
  const map = new Map<string, ClickElement>()
  for (const s of sources) {
    for (const t of clickTargets(s)) {
      const e = map.get(t.key) ?? { key: t.key, target: t.target, secondary: t.secondary, byVersion: {}, measure: t.visitors == null ? "events" : "visitors", total: 0 }
      const v = t.visitors ?? t.events
      e.byVersion[s.letter] = (e.byVersion[s.letter] ?? 0) + v
      e.total += v
      map.set(t.key, e)
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

// ── Support ─────────────────────────────────────────────────────────────

/** The backend can store a results goal and compute click sets (goalMetrics v2). */
export const supportsResultsGoal = (sources: VariantSource[]) =>
  sources.length > 0 && sources.every((s) => (s.goalMetrics?.version ?? 0) >= 2)

// ── Selecting a goal's numbers ──────────────────────────────────────────

export type GoalMetricKind = "proportion" | "count" | "descriptive"
export type GoalUnit = "percent" | "perVisitor" | "money"

export interface GoalVariantValue {
  id: string
  label: string
  letter: string
  isControl: boolean
  /** Numerator: visitors who converted (proportion), events (count) or dollars (descriptive). */
  events: number
  /** Unique Visitors in the version. */
  exposure: number
}

export interface GoalMetricResult {
  goal: ResultsGoal | null
  /** Plain goal name: "Sign-up button clicks". */
  label: string
  /** Plural noun for sentences: "visitors who clicked a sign-up button". */
  noun: string
  metric: GoalMetricKind
  unit: GoalUnit
  isLaunch: boolean
  /** Trials, payments, revenue: Stripe outcomes that settle 14 days later. */
  stripe: boolean
  available: boolean
  reason?: string
  /** Where the numbers came from. */
  source: "launch" | "goalMetrics" | "fallback"
  hint?: string
  /** Money goals: currency code, null when unknown or mixed (no symbol); undefined on today's API ($). */
  currency?: string | null
  variants: GoalVariantValue[]
}

const values = (sources: VariantSource[], pick: (s: VariantSource) => number) =>
  sources.map((s) => ({ id: s.id, label: s.label, letter: s.letter, isControl: s.isControl, events: pick(s), exposure: s.visitors }))

function sharedCurrency(sources: VariantSource[]): string | null {
  const codes = new Set(sources.map((s) => s.goalMetrics?.payments?.currency).filter((c): c is string => Boolean(c)))
  return codes.size === 1 ? [...codes][0].toUpperCase() : null
}

const LOADING = "Couldn't load this goal's numbers yet"
const NO_MATCH: Record<ClickPreset, string> = {
  signup: "No sign-up buttons clicked yet",
  demo: "No demo buttons clicked yet",
  key: "No key buttons clicked yet",
  all: "No clicks recorded yet",
  custom: "None of these elements clicked yet",
}

/**
 * The per-version numbers for a goal (null = the launch goal as launched).
 * Exposure is always the primary request's sessions. Proportion goals use the
 * nested unique visitors; a click set uses the backend's deduped `clickSet`
 * visitors. Without it, "All clicks" falls back to every click event and other
 * sets to summed click events (count test); unique visitors are never summed
 * across elements.
 */
export function selectGoalMetric(
  input: ResultsGoal | null,
  ctx: GoalContext,
  sources: VariantSource[],
  opts: { isLaunch?: boolean; nameOf?: (t: ClickSetTarget) => string } = {},
): GoalMetricResult {
  const launchGoal = launchResultsGoal(ctx)
  const isLaunch = opts.isLaunch ?? (!input || sameGoal(input, launchGoal))
  const goal = isLaunch ? launchGoal : input
  const label = goalName(goal, ctx, opts.nameOf)
  const full = sources.length > 0 && sources.every((s) => s.goalMetrics)
  const gm = (s: VariantSource) => s.goalMetrics!
  const base = { goal, label, isLaunch, stripe: false, available: true, source: "goalMetrics" as GoalMetricResult["source"] }
  const missing = { available: false, reason: LOADING, source: "fallback" as const }

  if (isLaunch) {
    const copy = launchGoalCopy(ctx.launchGoal)
    const count = isCountGoal(ctx.launchGoal)
    return {
      ...base,
      source: "launch",
      noun: copy.noun,
      metric: count ? "count" : "proportion",
      unit: count ? "perVisitor" : "percent",
      stripe: ctx.launchGoal === "revenue",
      hint: count ? "every click on the page counts" : ctx.finishPageUrl || undefined,
      variants: values(sources, (s) => s.launchEvents),
    }
  }

  switch (goal!.type) {
    case "clicks": {
      const set = goal!.clicks ?? { preset: "all", targets: [] }
      // A backend without the "key" preset is asked for its matches as a custom
      // set (useExperimentDetailModel), so "custom" answers "key" there.
      const answers = (p: ClickPreset) => p === set.preset || (set.preset === "key" && p === "custom")
      const deduped = full && sources.every((s) => gm(s).clickSet && answers(gm(s).clickSet!.preset))
      const noun =
        set.preset === "signup"
          ? "visitors who clicked a sign-up button"
          : set.preset === "demo"
            ? "visitors who clicked a demo button"
            : set.preset === "key"
              ? "visitors who clicked a key button"
            : set.preset === "all"
              ? "visitors who clicked"
              : "visitors who clicked these elements"
      if (deduped) {
        return { ...base, noun, metric: "proportion", unit: "percent", variants: values(sources, (s) => gm(s).clickSet!.visitors) }
      }
      // One matching element: its own unique visitors are exact, no clickSet needed.
      if (full && set.preset !== "all") {
        const keys = new Set(sources.flatMap((s) => clickTargets(s).filter((t) => clickSetIncludes(set, t)).map((t) => t.key)))
        if (keys.size === 1) {
          const [only] = [...keys]
          return {
            ...base,
            noun,
            metric: "proportion",
            unit: "percent",
            variants: values(sources, (s) => clickTargets(s).find((t) => t.key === only)?.visitors ?? 0),
          }
        }
      }
      if (set.preset === "all") {
        if (full) {
          return { ...base, noun, metric: "proportion", unit: "percent", variants: values(sources, (s) => gm(s).clicks.visitors) }
        }
        if (sources.some((s) => s.totalClicks != null)) {
          return {
            ...base,
            source: "fallback",
            noun: "clicks",
            metric: "count",
            unit: "perVisitor",
            hint: "every click on the page counts",
            variants: values(sources, (s) => s.totalClicks ?? 0),
          }
        }
        return { ...base, ...missing, noun, metric: "proportion", unit: "percent", variants: values(sources, () => 0) }
      }
      const anyTargets = sources.some((s) => clickTargets(s).length > 0)
      const anyMatch = sources.some((s) => clickTargets(s).some((t) => clickSetIncludes(set, t)))
      return {
        ...base,
        source: "fallback",
        available: anyTargets && anyMatch,
        reason: !anyTargets ? LOADING : !anyMatch ? NO_MATCH[set.preset] : undefined,
        noun: "clicks",
        metric: "count",
        unit: "perVisitor",
        hint: "clicks summed across the elements",
        variants: values(sources, (s) => clickTargets(s).reduce((sum, t) => (clickSetIncludes(set, t) ? sum + t.events : sum), 0)),
      }
    }
    case "forms":
      return full
        ? { ...base, noun: "visitors who submitted a form", metric: "proportion", unit: "percent", variants: values(sources, (s) => gm(s).forms.visitors) }
        : { ...base, ...missing, noun: "form submissions", metric: "proportion", unit: "percent", variants: values(sources, () => 0) }
    case "destination": {
      const path = goal!.destination ?? ""
      // The backend echoes the trimmed input as `path`.
      const matches = (s: VariantSource) =>
        s.goalMetrics?.destination && normalizeDestination(s.goalMetrics.destination.path) === normalizeDestination(path)
      const ready = Boolean(path) && sources.length > 0 && sources.every(matches)
      const external = isExternalDestination(path)
      return {
        ...base,
        available: ready,
        reason: !path ? "Enter a page path or link" : ready ? undefined : LOADING,
        noun: external ? `visitors who clicked through to ${path}` : `visitors who reached ${path || "the page"}`,
        metric: "proportion",
        unit: "percent",
        hint: "visit order is not checked",
        variants: values(sources, (s) => (matches(s) ? s.goalMetrics!.destination!.visitors : 0)),
      }
    }
    case "trials": {
      const ok = sources.length > 0 && sources.every((s) => s.goalMetrics?.trials || s.trials != null)
      return {
        ...base,
        source: full && sources.every((s) => gm(s).trials) ? "goalMetrics" : "fallback",
        available: ok,
        reason: ok ? undefined : "No Stripe source on this property",
        noun: "visitors who started a trial",
        metric: "proportion",
        unit: "percent",
        stripe: true,
        hint: "Stripe",
        variants: values(sources, (s) => s.goalMetrics?.trials?.visitors ?? s.trials ?? 0),
      }
    }
    case "payments":
    case "revenue_per_visitor": {
      const ok = sources.length > 0 && sources.every((s) => s.goalMetrics?.payments || s.payments != null)
      const fromGm = full && sources.every((s) => gm(s).payments)
      const shared = {
        ...base,
        source: (fromGm ? "goalMetrics" : "fallback") as GoalMetricResult["source"],
        available: ok,
        reason: ok ? undefined : "No Stripe source on this property",
        stripe: true,
      }
      if (goal!.type === "payments") {
        return {
          ...shared,
          noun: "visitors who paid",
          metric: "proportion",
          unit: "percent",
          hint: "Stripe",
          variants: values(sources, (s) => s.goalMetrics?.payments?.visitors ?? s.payments ?? 0),
        }
      }
      return {
        ...shared,
        noun: "revenue per visitor",
        metric: "descriptive",
        unit: "money",
        hint: "Stripe · descriptive only, no significance test",
        currency: fromGm ? sharedCurrency(sources) : undefined,
        variants: values(sources, (s) => (s.goalMetrics?.payments ? s.goalMetrics.payments.revenueCents / 100 : (s.revenue ?? 0))),
      }
    }
  }
}

// ── Comparing versions ──────────────────────────────────────────────────

/** Chance a version must reach before a goal can be called (and turn orange). */
export const CALL_CHANCE = 0.95

export interface GoalRow extends GoalVariantValue {
  /** events / exposure: a share (proportion), events per visitor (count) or dollars per visitor. */
  rate: number
  /** Observed relative change vs Control in percent; null for Control or when Control is 0. */
  change: number | null
  /** Control at 0 and this version above it: a change with no size. */
  isNew: boolean
  /** Bayesian P(version beats Control), 0 to 1; null for Control and descriptive goals. */
  chance: number | null
  /** 95% credible interval of the relative change, in percent (median as `point`). */
  range: { lower: number; upper: number; point: number } | null
  /**
   * P(this version is the best of all versions). Two versions: the challenger's
   * `chance` (Control gets 1 − chance). Three or more: joint draws (bayes.ts
   * `chanceBest`). Null for descriptive goals.
   */
  best: number | null
}

export interface GoalComparison {
  rows: GoalRow[]
  control: GoalRow | null
  /** The version the answer is about: the challenger most likely to be the best. */
  focus: GoalRow | null
  /** A challenger whose confidence (`confidenceOf`) is at CALL_CHANCE (never for descriptive goals). Call gates are separate. */
  winner: GoalRow | null
  /** Three or more versions: confidence is "best of all", not "beats Control". */
  multi: boolean
  /** The focus is at CALL_CHANCE (either way: B beats Control, or Control beats B). */
  decisive: boolean
  hasData: boolean
}

/**
 * Every version against Control: proportion goals on Beta-Binomial, count
 * goals on Gamma-Poisson with the dispersion inflation (bayes.ts). Descriptive
 * goals (revenue per visitor) get rates and changes only.
 */
export function compareGoal(
  result: Pick<GoalMetricResult, "metric" | "variants">,
  { dispersion = 1 }: { dispersion?: number } = {},
): GoalComparison {
  const metric = result.metric
  const control = result.variants.find((v) => v.isControl) ?? result.variants[0]
  const cRate = control && control.exposure > 0 ? control.events / control.exposure : 0

  const rows: GoalRow[] = result.variants.map((v) => {
    const rate = v.exposure > 0 ? v.events / v.exposure : 0
    if (!control || v.id === control.id) return { ...v, rate, change: null, isNew: false, chance: null, range: null, best: null }
    const change = cRate > 0 ? ((rate - cRate) / cRate) * 100 : null
    const isNew = cRate === 0 && rate > 0
    if (metric === "descriptive") return { ...v, rate, change, isNew, chance: null, range: null, best: null }
    const b =
      metric === "count"
        ? compareRates(control.events, control.exposure, v.events, v.exposure, dispersion)
        : compareProportions(control.events, control.exposure, v.events, v.exposure)
    return {
      ...v,
      rate,
      change,
      isNew,
      chance: b.chance,
      range: b.range && control.exposure > 0 && v.exposure > 0 ? { lower: b.range.lower, upper: b.range.upper, point: b.range.median } : null,
      best: b.chance,
    }
  })

  const controlRow = rows.find((r) => control && r.id === control.id) ?? null
  const challengers = rows.filter((r) => r !== controlRow)
  const multi = challengers.length >= 2
  if (metric !== "descriptive" && controlRow) {
    if (multi) {
      const p = chanceBest(rows, metric === "count" ? "count" : "proportion", dispersion)
      rows.forEach((r, i) => (r.best = p[i]))
    } else if (challengers[0]?.chance != null) {
      controlRow.best = 1 - challengers[0].chance
    }
  }
  const top = [...challengers].sort((x, y) => (y.best ?? 0) - (x.best ?? 0))[0] ?? null
  const winner = metric !== "descriptive" && top && (top.best ?? 0) >= CALL_CHANCE ? top : null
  // With several challengers, the answer is about the strongest one.
  const focus = metric === "descriptive" ? (challengers[0] ?? null) : top
  const decisive =
    focus?.best != null && (focus.best >= CALL_CHANCE || (!multi && focus.best <= 1 - CALL_CHANCE) || (multi && (controlRow?.best ?? 0) >= CALL_CHANCE))
  return { rows, control: controlRow, focus, winner, decisive, multi, hasData: rows.some((r) => r.exposure > 0) }
}

/** The call's confidence for a version: P(beats Control) with two versions, P(best of all) with more. */
export const confidenceOf = (row: GoalRow | null | undefined): number | null => row?.best ?? null

// ── Formatting ──────────────────────────────────────────────────────────

export function formatGoalRate(rate: number, unit: GoalUnit, currency?: string | null): string {
  if (unit === "percent") return `${(rate * 100).toFixed(1)}%`
  if (unit === "perVisitor") return rate.toFixed(2)
  return formatMoney(rate, currency)
}

/** Dollars when the currency was never reported (today's API), the code's symbol when known, no symbol when null. */
export function formatMoney(value: number, currency?: string | null): string {
  if (currency === undefined) return `$${value.toFixed(2)}`
  if (currency === null) return value.toFixed(2)
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency}`
  }
}

export function formatChange(change: number | null, isNew = false): string {
  if (isNew) return "new"
  if (change == null) return "—"
  return `${change > 0 ? "+" : change < 0 ? "−" : ""}${Math.abs(change).toFixed(1)}%`
}


// ── The one answer line ─────────────────────────────────────────────────

export interface GoalAnswer {
  /** The big value: a relative change, a dollar figure or a dash. */
  number: string
  /** What the number compares: "AI outcome H1 vs Control". */
  versus?: string
  /** The confidence sentence, or why there is no number. */
  label: string
  /** "likely +5% to +87%". */
  detail?: string
  /**
   * `answer` is the view's one orange: confidence ≥ 95%, every call gate met
   * for this goal's data, settled (Stripe goals) and test health not Check
   * (`callable`). Everything else is ink.
   */
  tone: "answer" | "ink" | "muted"
}

export function goalAnswer(
  result: GoalMetricResult,
  comparison: GoalComparison,
  {
    settling,
    callable,
  }: {
    isActive?: boolean
    settling?: { settling: boolean; settlesOn: Date }
    /** Confidence, call gates, settling and health all allow a call on this goal. */
    callable: boolean
  },
): GoalAnswer {
  const { focus, control, multi } = comparison
  if (!result.available) return { number: "—", label: result.reason ?? "Not available for this test", tone: "muted" }
  if (!comparison.hasData || !focus || !control) return { number: "—", label: "No visitors yet", tone: "muted" }
  const fr = (r: number) => formatGoalRate(r, result.unit, result.currency)
  const change = formatChange(focus.change, focus.isNew)
  const versus = `${focus.label} vs ${control.label}`
  const likely = focus.range ? `likely ${formatRange(focus.range)}` : undefined

  if (result.stripe && settling?.settling) {
    const date = settling.settlesOn.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    return { number: change, versus, label: `Settles ${date}`, detail: likely, tone: "muted" }
  }
  if (result.metric === "descriptive") {
    return { number: fr(focus.rate), versus: `${focus.label} · ${control.label} ${fr(control.rate)}`, label: change, tone: "ink" }
  }
  if (focus.events === 0 && control.events === 0) {
    return { number: "—", label: `No ${result.noun} yet`, tone: "muted" }
  }
  const confidence = focus.best ?? 0.5
  const controlBest = control.best ?? 1 - confidence
  const label =
    !multi && confidence <= 1 - CALL_CHANCE
      ? `${formatChance(controlBest)} confidence ${control.label} is better`
      : multi
        ? `${formatChance(confidence)} confidence ${focus.label} is the best version`
        : `${formatChance(confidence)} confidence ${focus.label} beats ${control.label}`
  return { number: change, versus, label, detail: likely, tone: callable && confidence >= CALL_CHANCE ? "answer" : "ink" }
}

/** "Launched with Form submissions." when the goal on screen is another one. */
export function launchedWithNote(isLaunch: boolean, ctx: GoalContext): string | null {
  return isLaunch ? null : `Launched with ${launchGoalCopy(ctx.launchGoal).label.toLowerCase()}.`
}

// ── The goal list ───────────────────────────────────────────────────────

/** Stable key for a goal, used by `?goal=` and the list. */
export function goalKey(goal: ResultsGoal | null): string {
  if (!goal) return "launch"
  if (goal.type === "clicks") {
    const set = goal.clicks ?? { preset: "all", targets: [] }
    if (set.preset !== "custom") return `clicks:${set.preset}`
    const ids = set.targets.map((t) => clickKey(t.target, t.secondary)).sort().join("|")
    return `clicks:custom:${hashString(ids)}`
  }
  if (goal.type === "destination") return `destination:${normalizeDestination(goal.destination)}`
  return goal.type
}

/** Small, stable string hash (FNV-1a, base 36). */
function hashString(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

export interface GoalCandidate {
  key: string
  /** null: a launch goal with no results-goal equivalent (session_time, bounce_rate). */
  goal: ResultsGoal | null
  isLaunch: boolean
  isPrimary: boolean
  /** An unsaved custom click set from the editor. */
  isDraft?: boolean
}

/**
 * Every goal a test can be read on, one row each: Key clicks, Click-through,
 * forms, saved / draft custom click sets, destinations (the launch one and a
 * saved one), and the Stripe goals when the property has a revenue source (or
 * when today's API can't say, so they read "not collected"). A launch goal
 * with no results-goal equivalent (engaged visits) comes first. Saved
 * sign-up / demo presets from before Key clicks still get their own row.
 */
export function goalCandidates({
  ctx,
  primary,
  customSets = [],
  draft = null,
  stripe,
}: {
  ctx: GoalContext
  /** The saved primary goal (effective), null for an unmapped launch goal. */
  primary: ResultsGoal | null
  customSets?: ResultsGoal[]
  draft?: ResultsGoal | null
  stripe: { trials: boolean; payments: boolean }
}): GoalCandidate[] {
  const launch = launchResultsGoal(ctx)
  const list: GoalCandidate[] = []
  const add = (goal: ResultsGoal | null, extra: Partial<GoalCandidate> = {}) => {
    const key = goalKey(goal)
    if (list.some((c) => c.key === key)) return
    const isLaunch = goal == null ? launch == null : sameGoal(goal, launch)
    list.push({ key, goal, isLaunch, isPrimary: key === goalKey(primary), ...extra })
  }
  // A launch goal with no results-goal equivalent (engaged visits) leads.
  if (!launch) add(null)
  add({ type: "clicks", clicks: { preset: "key", targets: [] } })
  add({ type: "clicks", clicks: { preset: "all", targets: [] } })
  add({ type: "forms" })
  for (const g of customSets) add(g)
  if (launch?.type === "destination") add(launch)
  if (primary?.type === "destination") add(primary)
  if (stripe.trials) add({ type: "trials" })
  if (stripe.payments) {
    add({ type: "payments" })
    add({ type: "revenue_per_visitor" })
  }
  if (launch) add(launch)
  if (primary) add(primary)
  if (draft) add(draft, { isDraft: true })
  return list
}

/** The row `?goal=` points at, else the primary. */
export function selectedCandidate(candidates: GoalCandidate[], param: string | null | undefined): GoalCandidate | undefined {
  return candidates.find((c) => c.key === param) ?? candidates.find((c) => c.isPrimary) ?? candidates[0]
}
